// Replay (v2) routing recommendations, shared by the Node routes and the browser tab, so the page can recompute as
// the margin changes. Dependency-free: it is bundled into the browser build as well as the server.
//
// The evidence is taskreplay_routing (eval/taskreplay/replay/routing.py): per slice of the routable dimensions and
// per two models, how far the cheaper (candidate) is behind the dearer (baseline) on the combined rating, with its
// spread over resampled tasks, and what each costs per task. A rule is a slice where the gap's lower bound clears
// the margin and the candidate saves enough; the narrower the slice, the stronger the evidence asked for, since the
// more slices are searched the more pass by chance.

export type Dimension = 'task_type' | 'complexity' | 'repo'
export const DIMENSIONS: Dimension[] = ['task_type', 'complexity', 'repo']

/** Values of the routable dimensions; {} is every task in the pool. */
export type Slice = Partial<Record<Dimension, string>>

export interface RoutingRow {
  slice: Slice
  baseline: string
  candidate: string
  tasks: number
  /** candidate − baseline on the combined rating, in Elo; negative: behind. */
  gap: number
  /** The gap at each quantile over resampled tasks, keyed "0.005" … "0.995". */
  quantiles: Record<string, number>
  parts: {
    pairwise: number | null
    per_cell: number | null
    judge: { wins: number; ties: number; losses: number }
    scores: { baseline: number; candidate: number }
  }
  cost: { baseline: number; candidate: number; savings: number | null }
}

/** How far behind the baseline a candidate may be, in Elo: 35 is an expected score of 0.45 against it, about a
 *  10-point mean score gap on the cells' own scores. */
export const DEFAULT_MARGIN = 35
/** The least share of the baseline's cost per task a rule must save. */
export const DEFAULT_MIN_SAVINGS = 0.2

/** The quantile a slice's bound is read at, by how many dimensions it fixes: 95% for the whole pool or one
 *  dimension, 99% for two, 99.5% for three. */
const LEVEL: Record<number, string> = { 0: '0.05', 1: '0.05', 2: '0.01', 3: '0.005' }

export const level = (slice: Slice): number => 1 - Number(LEVEL[depth(slice)] ?? '0.005')
export const depth = (slice: Slice): number => DIMENSIONS.filter((d) => slice[d]).length

/** The candidate's expected score against the baseline in one task from the slice (a win 1, a tie ½, partial
 *  results between), from a gap in Elo. */
export const expectedScore = (gap: number): number => 1 / (1 + 10 ** (-gap / 400))

/** The share of tasks on which the candidate's work is judged as good as the baseline's or better, at a gap in Elo:
 *  the expected score counts a tie as half, this counts it whole, at the tie rate of the judge's `record` of the two
 *  (wins, ties and losses from the candidate's side). Null without a record. */
export function asGoodOrBetter(gap: number, record: { wins: number; ties: number; losses: number }): number | null {
  const n = record.wins + record.ties + record.losses
  if (!n) return null
  const ties = record.ties / n
  return Math.min(1, Math.max(0, expectedScore(gap) - ties / 2) + ties)
}

/** The gap's lower bound at the slice's level: how far behind the candidate could be. */
export function bound(row: RoutingRow): number {
  const q = LEVEL[depth(row.slice)] ?? '0.005'
  return row.quantiles[q] ?? row.gap
}

/** Whether `a` is a broader slice than `b`: every value it fixes, `b` fixes the same, and `b` fixes more. */
export function broader(a: Slice, b: Slice): boolean {
  return depth(a) < depth(b) && DIMENSIONS.every((d) => !a[d] || a[d] === b[d])
}

export interface Recommendation {
  row: RoutingRow
  bound: number
  level: number
  /** route: the bound clears the margin; inconclusive: the gap does, the bound not yet. */
  verdict: 'route' | 'inconclusive'
  /** inconclusive: about how many more tasks would settle it if the gap held. */
  moreTasks?: number
}

/** About how many more tasks the slice needs for its bound to clear the margin, if the gap holds (the bound's
 *  distance from the gap shrinks with the square root of the tasks); null when the gap itself is past the margin. */
export function tasksToSettle(row: RoutingRow, margin: number): number | null {
  const now = row.gap - bound(row)
  const room = row.gap + margin
  if (room <= 0 || now <= 0) return null
  return Math.max(0, Math.ceil(row.tasks * (now / room) ** 2 - row.tasks))
}

/**
 * The routing rules the evidence supports for traffic now on `baseline`: a slice and a cheaper candidate that saves
 * at least `minSavings` and whose bound clears `margin`. Only the broadest slice is kept: a slice is dropped when a
 * broader one already routes to the same candidate, or to one at least as cheap. Slices that are close (the gap
 * clears the margin, the bound not yet) come back as inconclusive, with how many more tasks would settle them.
 */
export function recommend(
  rows: RoutingRow[],
  opts: { baseline: string; margin?: number; minSavings?: number },
): { routes: Recommendation[]; inconclusive: Recommendation[] } {
  const margin = opts.margin ?? DEFAULT_MARGIN
  const minSavings = opts.minSavings ?? DEFAULT_MIN_SAVINGS
  const mine = rows.filter((r) => r.baseline === opts.baseline && (r.cost.savings ?? 0) >= minSavings)
  // A model's cost per task over the whole pool where it is known, else its mean over the rows: "at least as cheap".
  const costOf = new Map<string, number>()
  for (const r of mine) {
    if (!depth(r.slice) || !costOf.has(r.candidate)) costOf.set(r.candidate, r.cost.candidate)
  }
  const cheapAsOrCheaper = (a: string, b: string) => a === b || (costOf.get(a) ?? Infinity) <= (costOf.get(b) ?? Infinity)

  const judged = mine.map((row): Recommendation => {
    const b = bound(row)
    return { row, bound: b, level: level(row.slice), verdict: b >= -margin ? 'route' : 'inconclusive' }
  })
  const routes = judged.filter((r) => r.verdict === 'route')
  const covered = (r: Recommendation) =>
    routes.some((o) => o !== r && broader(o.row.slice, r.row.slice) && cheapAsOrCheaper(o.row.candidate, r.row.candidate))
  const impact = (r: Recommendation) => -(r.row.tasks * (r.row.cost.savings ?? 0))

  return {
    routes: routes.filter((r) => !covered(r)).sort((a, b) => impact(a) - impact(b)),
    inconclusive: judged
      .filter((r) => r.verdict === 'inconclusive' && r.row.gap > -margin && !covered(r))
      .map((r) => ({ ...r, moreTasks: tasksToSettle(r.row, margin) ?? undefined }))
      .sort((a, b) => (a.moreTasks ?? Infinity) - (b.moreTasks ?? Infinity)),
  }
}

/** The model traffic moves from unless the reader picks another: the dearest model the evidence has as a baseline
 *  (its cost per task over all the tasks where known). */
export function defaultBaseline(rows: RoutingRow[]): string {
  const cost = new Map<string, number>()
  for (const r of rows) if (!depth(r.slice)) cost.set(r.baseline, Math.max(cost.get(r.baseline) ?? 0, r.cost.baseline))
  for (const r of rows) if (!cost.has(r.baseline)) cost.set(r.baseline, r.cost.baseline)
  return [...cost.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? ''
}

/** A slice as words: "coding:explore · routine", or "all tasks". */
export function sliceLabel(slice: Slice): string {
  const parts = DIMENSIONS.map((d) => slice[d]).filter(Boolean)
  return parts.length ? parts.join(' · ') : 'all tasks'
}

// ---- building and sizing a dataset ------------------------------------------------------------------------------

/** The models a run's support agents (grader, simulated user, environment agent) can take. */
export const SUPPORT_MODELS: { id: string; label: string }[] = [
  { id: 'us.anthropic.claude-opus-5-5', label: 'Opus 5.5' },
  { id: 'gpt-6.1-sol', label: 'GPT 6.1 Sol' },
]
export type SupportRole = 'grader' | 'simulator' | 'environment'
export const SUPPORT_ROLES: SupportRole[] = ['grader', 'simulator', 'environment']

/** Candidate models offered in the run dialog: "tuneloop/" and a Tuneloop gateway id (eval/taskreplay/config.py
 *  GATEWAY_MODELS), each run in Claude Code through the gateway. Any other id is accepted too (it runs on Bedrock). */
export const RUN_MODELS = [
  'bedrock/claude-opus-5-5[1m]', 'bedrock/claude-sonnet-5-5[1m]', 'bedrock/claude-haiku-5-5[1m]',
  'openai-platform/gpt-6.1-sol[1m]', 'openai-platform/gpt-6-luna',
  'fireworks/deepseek-v4.1-flash[1m]', 'fireworks/glm-5.3[1m]', 'fireworks/qwen3.8-max[1m]',
  'meta/muse-wonder-1.0[1m]', 'meta/muse-spark-1.3[1m]', 'gemini/gemini-4-argon[1m]', 'fireworks/muse-spark-open[1m]',
  'fireworks/deepseek-v4.1-flash-1017[1m]',
].map((m) => 'tuneloop/' + m)

/** One-sided 95%: the question a dataset answers is how far behind a cheaper model could be. */
export const PLAN_Z = 1.645
/** The spread of a per-task gap, in Elo, to plan with before a pool has evidence of its own. */
export const DEFAULT_SD = 140

/** The half-width of a gap's interval after `tasks` tasks: z · sd / √tasks (halving it takes four times the tasks). */
export const halfWidth = (tasks: number, sd: number): number => (tasks > 0 ? (PLAN_Z * sd) / Math.sqrt(tasks) : Infinity)
/** The tasks a half-width asks for. */
export const tasksFor = (half: number, sd: number): number => Math.ceil(((PLAN_Z * sd) / half) ** 2)

export interface Sampleable { id: string; taskType: string; complexity: string; repo: string; session: string }

/** FNV-1a: a fixed order for the sampler, so the same filters give the same sample. */
function order(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0
  return h
}

/**
 * Up to `n` tasks spread across type × complexity × repo: one from each stratum in turn, at most `perSession` from
 * any one session (raised a step at a time only when nothing else is left), each stratum in a fixed order.
 */
export function spread<T extends Sampleable>(pool: T[], n: number, perSession = 2): T[] {
  const strata = new Map<string, T[]>()
  for (const t of [...pool].sort((a, b) => order(a.id) - order(b.id) || a.id.localeCompare(b.id))) {
    const key = t.taskType + '\n' + t.complexity + '\n' + t.repo
    strata.set(key, [...(strata.get(key) ?? []), t])
  }
  const queues = [...strata.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0])).map((e) => e[1])
  const taken: T[] = []
  const per = new Map<string, number>()
  for (let cap = perSession; taken.length < n && queues.some((q) => q.length); cap++) {
    for (let moved = true; moved && taken.length < n;) {
      moved = false
      for (const q of queues) {
        const i = q.findIndex((t) => (per.get(t.session) ?? 0) < cap)
        if (i < 0) continue
        const t = q.splice(i, 1)[0]!
        per.set(t.session, (per.get(t.session) ?? 0) + 1)
        taken.push(t)
        moved = true
        if (taken.length >= n) break
      }
    }
  }
  return taken
}

/** What estimates start from (the store's ReplayPlanning). */
export interface Planning {
  sd: number | null
  agentCost: Record<string, Record<string, number>>
  gradeCost: number | null
  harnessCost: number | null
  judgeCost: number | null
  models: string[]
}

export interface Estimate {
  /** Each model's agent cost over the tasks (null: never run, no estimate). */
  agent: Record<string, number | null>
  /** Grading and the replay's own agents, per cell. */
  grading: number
  /** The pairwise judge: each new pair of models on each task. */
  judging: number
  total: number
}

/**
 * What running `models` on `tasks` costs, from the averages so far: each model's agent cost by task type (its mean
 * over every type where a type is new to it), grading and the replay's own agents per cell, and the judge per pair
 * (between the new models, and each new model with the `existing` ones already on the tasks).
 */
export function estimate(tasks: { taskType: string }[], models: string[], p: Planning, existing: string[] = []): Estimate {
  const agent: Record<string, number | null> = {}
  for (const m of models) {
    const by = p.agentCost[m]
    agent[m] = by ? tasks.reduce((a, t) => a + (by[t.taskType] ?? by[''] ?? 0), 0) : null
  }
  const cells = tasks.length * models.length
  const grading = cells * ((p.gradeCost ?? 0) + (p.harnessCost ?? 0))
  const others = existing.filter((m) => models.indexOf(m) < 0).length
  const pairs = (models.length * (models.length - 1)) / 2 + models.length * others
  const judging = tasks.length * pairs * (p.judgeCost ?? 0)
  const total = Object.values(agent).reduce<number>((a, v) => a + (v ?? 0), 0) + grading + judging
  return { agent, grading, judging, total }
}
